import React from "react";
import { Banknote, Percent, CalendarClock, ShieldAlert, AlertTriangle, Coins } from "lucide-react";
import { fmtMoney, DEFAULT_CURRENCY } from "../lib/money";
import { useTranslation } from "react-i18next";

const fmtPct = (n) => (n == null || isNaN(n) ? "—" : `${Number(n).toFixed(2)}%`);

const OPERATOR_DISPLAY = { ">=": "≥", "<=": "≤", "==": "=", ">": ">", "<": "<" };

/**
 * Does this financing carry an identified physical asset (vehicle, equipment)?
 * Exported so the branch is unit-testable without rendering the panel.
 * Numeric fields are compared to null so a legitimate 0 still counts.
 */
export function hasFinancedAsset(enrichment) {
  const e = enrichment || {};
  return Boolean(
    e.asset_description ||
      e.asset_serial_number ||
      e.residual_value != null ||
      e.mileage_cap_km != null ||
      e.buyout_option?.available,
  );
}

export default function FinancingEnrichmentPanel({ enrichment }) {
  const { t } = useTranslation("components");
  const e = enrichment || {};
  const currency = (e.currency || DEFAULT_CURRENCY).toUpperCase();
  const money = (n) => fmtMoney(n, currency) ?? "—";
  const pg = e.personal_guarantee;
  const pre = e.prepayment_penalty;
  return (
    <section
      className="bg-white border border-zinc-200 rounded-xl p-6"
      data-testid="financing-enrichment-panel"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className="w-7 h-7 rounded-md grid place-items-center bg-emerald-50 text-emerald-700">
          <Banknote size={14} strokeWidth={1.75} />
        </div>
        <h2 className="font-display font-semibold text-base tracking-tight">{t("enrichment.financing.section_title")}</h2>
      </div>

      {/* Headline numbers */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Tile label={t("enrichment.financing.lender")} value={e.lender || "—"} testid="fin-enrich-lender" />
        <Tile label={t("enrichment.financing.instrument")} value={e.instrument_type ? t(`enrichment.financing.instrument_type_options.${e.instrument_type}`, e.instrument_type.replace(/_/g, " ")) : "—"} testid="fin-enrich-instrument" />
        <Tile
          label={t("enrichment.financing.principal")}
          value={money(e.principal_amount)}
          accent="emerald"
          testid="fin-enrich-principal"
        />
        <Tile
          label={t("enrichment.financing.apr")}
          value={fmtPct(e.apr)}
          sub={e.interest_rate_type ? t(`enrichment.financing.interest_rate_type_options.${e.interest_rate_type}`, e.interest_rate_type) : null}
          icon={Percent}
          accent="amber"
          testid="fin-enrich-apr"
        />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Tile
          label={t("enrichment.financing.term")}
          value={
            e.term_months == null || isNaN(e.term_months)
              ? "—"
              : Number(e.term_months) % 12 === 0
              ? t("enrichment.financing.years", { count: Number(e.term_months) / 12 })
              : t("enrichment.financing.months", { count: Number(e.term_months) })
          }
          testid="fin-enrich-term"
        />
        <Tile
          label={t("enrichment.financing.payment")}
          value={money(e.payment_amount)}
          sub={e.payment_frequency ? t(`enrichment.financing.payment_frequency_options.${e.payment_frequency}`, e.payment_frequency) : null}
          testid="fin-enrich-payment"
        />
        <Tile label={t("enrichment.financing.maturity")} value={e.maturity_date || "—"} icon={CalendarClock} testid="fin-enrich-maturity" />
        <Tile
          label={t("enrichment.financing.total_cost_of_capital")}
          value={money(e.total_cost_of_capital)}
          accent="rose"
          testid="fin-enrich-tcc"
        />
      </div>

      {/* Risk-bearing clauses */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {pg && (
          <div
            className={`px-4 py-3 rounded-lg border ${
              pg.required
                ? pg.scope === "uncapped"
                  ? "bg-rose-50/60 border-rose-200"
                  : "bg-amber-50/60 border-amber-200"
                : "bg-emerald-50/40 border-emerald-200"
            }`}
            data-testid="fin-enrich-personal-guarantee"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <ShieldAlert
                size={13}
                className={pg.required && pg.scope === "uncapped" ? "text-rose-700" : "text-amber-700"}
              />
              <p className="text-[11px] uppercase tracking-wider font-semibold">{t("enrichment.financing.personal_guarantee")}</p>
            </div>
            <p className="text-sm text-zinc-800 leading-relaxed font-semibold">
              {pg.required
                ? pg.scope === "uncapped"
                  ? t("enrichment.financing.pg_uncapped")
                  : pg.scope === "capped"
                  ? t("enrichment.financing.pg_capped", { amount: money(pg.cap_amount) })
                  : t("enrichment.financing.pg_required")
                : t("enrichment.financing.pg_not_required")}
            </p>
            {pg.guarantors?.length > 0 && (
              <p className="text-xs text-zinc-600 mt-1">{t("enrichment.financing.guarantors_label")} {pg.guarantors.join(", ")}</p>
            )}
          </div>
        )}
        {pre && (
          <div
            className={`px-4 py-3 rounded-lg border ${pre.applies ? "bg-amber-50/60 border-amber-200" : "bg-emerald-50/40 border-emerald-200"}`}
            data-testid="fin-enrich-prepayment-penalty"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <AlertTriangle size={13} className={pre.applies ? "text-amber-700" : "text-emerald-700"} />
              <p className="text-[11px] uppercase tracking-wider font-semibold">{t("enrichment.financing.prepayment_penalty")}</p>
            </div>
            <p className="text-sm text-zinc-800 leading-relaxed font-semibold">
              {pre.applies
                ? pre.amount_pct
                  ? t("enrichment.financing.pre_applies_pct", { pct: pre.amount_pct })
                  : t("enrichment.financing.pre_applies")
                : t("enrichment.financing.pre_does_not_apply")}
            </p>
            {pre.applies && pre.schedule && (
              <p className="text-xs text-zinc-600 mt-1">{t("enrichment.financing.schedule_label")} {pre.schedule}</p>
            )}
          </div>
        )}
      </div>

      {/* Financial covenants */}
      {e.financial_covenants?.length > 0 && (
        <div className="mb-6" data-testid="fin-enrich-covenants">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">
            {t("enrichment.financing.covenants_label", { count: e.financial_covenants.length })}
          </p>
          <div className="space-y-1.5">
            {e.financial_covenants.map((c, i) => (
              <div
                key={i}
                data-testid={`fin-covenant-${i}`}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-200"
              >
                <div className="flex items-center gap-2">
                  <Coins size={12} className="text-zinc-500" />
                  <span className="font-mono text-xs text-zinc-900">
                    {c.metric.replace(/_/g, " ")} {OPERATOR_DISPLAY[c.operator] || c.operator} {c.threshold}
                    {c.threshold_unit ? c.threshold_unit : ""}
                  </span>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {t("enrichment.financing.tested_prefix")} {t(`enrichment.financing.covenant_tested_options.${c.tested}`, c.tested.replace(/_/g, " "))}
                  {c.cure_period_days ? ` · ${t("enrichment.financing.cure_period", { count: c.cure_period_days })}` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collateral */}
      {e.collateral?.length > 0 && (
        <div className="mb-6" data-testid="fin-enrich-collateral">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">
            {t("enrichment.financing.collateral_label", { count: e.collateral.length })}
          </p>
          <ul className="space-y-1.5">
            {e.collateral.map((c, i) => (
              <li
                key={i}
                className="text-sm px-3 py-2 rounded-lg border border-zinc-200 bg-white flex items-center justify-between"
                data-testid={`fin-collateral-${i}`}
              >
                <span>
                  <span className="font-semibold">{c.type}</span>
                  {c.description ? <span className="text-zinc-600"> — {c.description}</span> : null}
                </span>
                {c.value_estimate != null && (
                  <span className="font-mono text-xs tabular-nums text-zinc-500">{money(c.value_estimate)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Financed asset — vehicle / equipment */}
      {hasFinancedAsset(e) && (
        <div className="mb-6" data-testid="fin-enrich-asset">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">
            {t("enrichment.financing.asset_label")}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Tile
              label={t("enrichment.financing.asset_description")}
              value={e.asset_description || "—"}
              testid="fin-enrich-asset-description"
            />
            <Tile
              label={t("enrichment.financing.asset_serial_number")}
              value={e.asset_serial_number || "—"}
              testid="fin-enrich-asset-serial"
            />
            <Tile
              label={t("enrichment.financing.residual_value")}
              value={money(e.residual_value)}
              testid="fin-enrich-residual-value"
            />
            <Tile
              label={t("enrichment.financing.buyout_option")}
              value={e.buyout_option?.available ? money(e.buyout_option.amount) : "—"}
              sub={e.buyout_option?.timing ? t(`enrichment.financing.buyout_timing_options.${e.buyout_option.timing}`, e.buyout_option.timing) : null}
              testid="fin-enrich-buyout"
            />
            <Tile
              label={t("enrichment.financing.mileage_cap")}
              value={
                e.mileage_cap_km == null
                  ? "—"
                  : t("enrichment.financing.km_per_year", { count: Number(e.mileage_cap_km) })
              }
              testid="fin-enrich-mileage-cap"
            />
          </div>
        </div>
      )}

      {/* Fees */}
      {e.fees?.length > 0 && (
        <div className="mb-6" data-testid="fin-enrich-fees">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-medium">{t("enrichment.financing.fees_label")}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {e.fees.map((f, i) => (
              <div
                key={i}
                data-testid={`fin-fee-${i}`}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-200 text-sm"
              >
                <span className="font-medium text-zinc-900">{f.name}</span>
                <span className="font-mono text-xs tabular-nums text-zinc-700">
                  {money(f.amount)}
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 ml-1.5">{t(`enrichment.financing.fee_timing_options.${f.timing}`, f.timing)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk flags */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {e.cross_default_clause && <Flag tone="rose">{t("enrichment.financing.cross_default_clause")}</Flag>}
        {e.acceleration_clause_present && <Flag tone="amber">{t("enrichment.financing.acceleration_on_default")}</Flag>}
        {e.change_of_control_clause && <Flag tone="zinc">{t("enrichment.financing.change_of_control_clause")}</Flag>}
        {(e.use_of_funds_restrictions?.length || 0) > 0 && (
          <Flag tone="zinc">{t("enrichment.financing.use_of_funds", { count: e.use_of_funds_restrictions.length })}</Flag>
        )}
      </div>

      {e.summary && (
        <p className="text-sm text-zinc-700 leading-relaxed mt-3" data-testid="fin-enrich-summary">
          {e.summary}
        </p>
      )}
    </section>
  );
}

function Tile({ label, value, sub, icon: Icon, accent, testid }) {
  const accents = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1 flex items-center gap-1">
        {Icon && <Icon size={10} />}
        {label}
      </p>
      <p className={`font-display font-semibold text-zinc-950 tabular-nums ${accent ? accents[accent] : ""}`}>
        {value}
      </p>
      {sub && <p className="text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function Flag({ tone, children }) {
  const tones = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    zinc: "bg-zinc-100 text-zinc-700 border-zinc-200",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
}
